'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useLanguage } from '@/context/LanguageContext'
import { useCountries } from '@/context/CountriesContext'
import { useState, useEffect } from 'react'
import { SiTelegram, SiInstagram, SiTiktok, SiPinterest } from 'react-icons/si'

export default function Footer() {
    const { language } = useLanguage()
    const [currentYear, setCurrentYear] = useState('2026')
    const countries = useCountries()
    const pathname = usePathname()

    const pathParts = (pathname || '').split('/').filter(Boolean)
    // Язык из URL, а не из клиентского контекста — иначе футер мигает EN→RU
    // после гидрации при прямой загрузке RU-страницы.
    const lang = pathParts[0] === 'en' || pathParts[0] === 'ru' ? pathParts[0] : language
    const secondSegment = pathParts.length >= 2 ? pathParts[1] : ''
    const topLevelSections = new Set(['esim', 'vpn', 'tours', 'transport', 'stay', 'insurance', 'blog', 'privacy', 'disclaimer', 'cities', 'contact', 'tables'])
    const currentCountry = secondSegment && !topLevelSections.has(secondSegment) ? secondSegment : ''

    useEffect(() => {
        setCurrentYear(new Date().getFullYear().toString())
    }, [])

    // Страна статьи блога: её нет в пути (/ru/blog/<slug>), и «Сервисы» в подвале статьи о
    // Катаре или Грузии вели в Черногорию (ревью 30.09.2026). Статья помечает корень атрибутом.
    const [pageCountry, setPageCountry] = useState('')
    useEffect(() => {
        const el = typeof document !== 'undefined' ? document.querySelector('[data-page-country]') : null
        setPageCountry(el?.getAttribute('data-page-country') || '')
    }, [pathname])

    const getCountryName = (country) => {
        if (lang === 'ru' && country.nameRu) return country.nameRu
        if (lang === 'en' && country.nameEn) return country.nameEn
        return country.name || country.slug
    }

    // Сервисы ведут на страну: текущую, иначе первую активную
    const primaryCountry = currentCountry || pageCountry || countries[0]?.slug || ''
    const servicesData = [
        { key: 'esim', ru: 'eSIM', en: 'eSIM' },
        { key: 'vpn', ru: 'VPN', en: 'VPN' },
        { key: 'insurance', ru: 'Страховка', en: 'Insurance' },
        { key: 'tours', ru: 'Туры', en: 'Tours' },
        { key: 'transport', ru: 'Транспорт', en: 'Transport' },
        { key: 'stay', ru: 'Жильё', en: 'Stay' },
    ]

    const content = {
        ru: {
            about: 'Умный помощник для самостоятельных путешествий — без турагентов и переплат.',
            destinations: 'Направления',
            services: 'Сервисы',
            // короткие подписи для нижней строки (полные — в title для доступности)
            resourcesLinks: [
                { href: `/${lang}/blog`, label: 'Блог', full: 'Блог' },
                { href: `/${lang}/contact`, label: 'Контакты', full: 'Контакты' },
                { href: `/${lang}/privacy`, label: 'Политика', full: 'Политика конфиденциальности' },
                { href: `/${lang}/disclaimer`, label: 'Дисклеймер', full: 'Отказ от ответственности' },
            ],
            copyright: `© ${currentYear} TripLinkHub`,
        },
        en: {
            about: 'A smart companion for independent travel — no agencies, no markups.',
            destinations: 'Destinations',
            services: 'Services',
            resourcesLinks: [
                { href: `/${lang}/blog`, label: 'Blog', full: 'Blog' },
                { href: `/${lang}/contact`, label: 'Contact', full: 'Contact' },
                { href: `/${lang}/privacy`, label: 'Privacy', full: 'Privacy Policy' },
                { href: `/${lang}/disclaimer`, label: 'Disclaimer', full: 'Disclaimer' },
            ],
            copyright: `© ${currentYear} TripLinkHub`,
        },
    }
    const t = content[lang] || content.en

    const socialLinks = [
        { href: 'https://t.me/triplinkhub', label: 'Telegram', Icon: SiTelegram },
        { href: 'https://instagram.com/triplinkhub', label: 'Instagram', Icon: SiInstagram },
        { href: 'https://tiktok.com/@triplinkhub', label: 'TikTok', Icon: SiTiktok },
        { href: 'https://www.pinterest.com/TripLinkHub', label: 'Pinterest', Icon: SiPinterest },
    ]

    return (
        <footer className="gf-foot2 gf-foot3">
            {/* верх: бренд+соцсети слева, inline-кластеры ссылок справа */}
            <div className="gf-foot3-grid">
                <div className="lhs">
                    <div className="wm">Trip<span>Link</span>Hub</div>
                    <p>{t.about}</p>
                    <div className="gf-foot3-social">
                        {socialLinks.map((s) => (
                            <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label} title={s.label}>
                                <s.Icon aria-hidden />
                            </a>
                        ))}
                    </div>
                </div>

                <div className="gf-foot3-rows">
                    <div className="gf-foot3-row">
                        <span className="lbl">{t.destinations}</span>
                        <div className="links">
                            {countries.slice(0, 6).map((country) => (
                                <Link key={country.slug} href={`/${lang}/${country.slug}`}>{getCountryName(country)}</Link>
                            ))}
                        </div>
                    </div>

                    <div className="gf-foot3-row">
                        <span className="lbl">{t.services}</span>
                        <div className="links">
                            {servicesData.map((service) => (
                                <Link key={service.key} href={primaryCountry ? `/${lang}/${primaryCountry}/${service.key}` : `/${lang}`}>
                                    {lang === 'ru' ? service.ru : service.en}
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* низ: год + ресурс-ссылки (блог/контакты/политика/дисклеймер) + наверх */}
            <div className="gf-foot3-legal">
                <span className="cpr">{t.copyright}</span>
                <nav className="legal-links" aria-label={lang === 'ru' ? 'Документы и контакты' : 'Resources'}>
                    {t.resourcesLinks.map((item) => (
                        <Link key={item.href} href={item.href} title={item.full}>{item.label}</Link>
                    ))}
                </nav>
            </div>
        </footer>
    )
}
